import { ButtonBase } from '@mui/material';
import { useApp } from 'contexts/app';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Paper, Stack, styled, Tag, Text, ViewMore } from 'shell/components';
import { getTemplatedFeedback } from 'utils/helper';

const noFeedbackTranslationArgs = ['feedback-section.no-feedback', 'No feedback provided.'];

const FeedbackRenderer = ({ assignment, currentSubmission, selectedFeedbackId, setSelectedFeedbackId }) => {
    const { studentSubmissions } = useApp();
    const { t } = useTranslation('assignment');

    const grading = currentSubmission.ai.grading;
    const feedback = currentSubmission.ai.feedback;

    const isFeedbackGeneralDetailed = currentSubmission.ai.feedback.feedback_type === 'system';
    const isFeedbackRubric = currentSubmission.ai.feedback.feedback_type === 'rubric';
    const isFeedbackTemplated = currentSubmission.ai.feedback.feedback_type === 'templated';
    const isFeedbackCustom = currentSubmission.ai.feedback.feedback_type === 'custom';

    const currentAccountUserStudentSubmissions = studentSubmissions.filter(
        ({ owner_account_id }) => owner_account_id === currentSubmission.owner_account_id
    );

    const previousSubmissions = currentAccountUserStudentSubmissions.slice(
        0,
        currentAccountUserStudentSubmissions.findIndex(({ id }) => id === currentSubmission.id)
    );
    const previousCommentaries = previousSubmissions.map(({ ai }) => ai.feedback.commentary);

    if (!feedback) return null;

    if (isFeedbackTemplated) {
        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p1'} weight={'semibold'}>
                        {t('feedback-section.assignment-feedback-label', 'Assignment Feedback')}
                    </Text>
                </BackgroundStack>
                <Stack sx={{ flex: 1, position: 'relative', p: 3 }}>
                    {Object.keys(grading).length ? (
                        <Text rich>
                            {getTemplatedFeedback(assignment, { criteria: grading }, 'ai_student_feedback_settings')}
                        </Text>
                    ) : (
                        <CenterText align={'center'} color={'light'}>
                            {t(
                                'feedback-section.templated-feedback-description',
                                'This assignment is using Smart Feedback Templates. Feedback will appear here as you make grading selections.'
                            )}
                        </CenterText>
                    )}
                </Stack>
            </>
        );
    }

    return (
        <>
            {(isFeedbackGeneralDetailed || isFeedbackCustom) && (
                <>
                    <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Text variant={'p1'} weight={'semibold'}>
                            {t('feedback-section.general-feedback-label', 'General Feedback')}
                        </Text>
                        <Tag type={'ai'} value={'assisted'} />
                    </BackgroundStack>
                    {feedback.commentary ? (
                        <Stack sx={{ p: 3 }} spacing={3}>
                            <Text rich>{feedback.commentary}</Text>
                            {(isFeedbackGeneralDetailed || isFeedbackCustom) && (
                                <Stack spacing={2}>
                                    {previousCommentaries.reverse().map((feedback, index) => (
                                        <Stack key={index} direction={'row'} spacing={4}>
                                            <Stack sx={{ flexShrink: 0 }} alignItems={'center'} spacing={2}>
                                                <Tag type={'draft'} value={previousCommentaries.length - index} />
                                                <CustomBox />
                                            </Stack>
                                            <ViewMore
                                                sx={{ fontStyle: 'italic' }}
                                                TextComponentProps={{
                                                    color: 'light',
                                                    rich: true,
                                                }}
                                                variant={'primary'}
                                            >
                                                {`${feedback || t(...noFeedbackTranslationArgs)}`}
                                            </ViewMore>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    ) : (
                        <Divider />
                    )}
                </>
            )}
            {(isFeedbackGeneralDetailed || isFeedbackRubric) && (
                <>
                    <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Text variant={'p1'} weight={'semibold'}>
                            {isFeedbackGeneralDetailed
                                ? t('feedback-section.detailed-feedback-label', 'Detailed Feedback')
                                : t('feedback-section.assignment-feedback-label', 'Assignment Feedback')}
                        </Text>
                        {isFeedbackGeneralDetailed && <Tag type={'ai'} value={'assisted'} />}
                    </BackgroundStack>
                    <Stack sx={{ position: 'relative', flex: 1, p: 3 }} spacing={2.5}>
                        {feedback.improvement_suggestions
                            .filter(({ active }) => active)
                            .map((item) =>
                                isFeedbackGeneralDetailed ? (
                                    <DetailedFeedback
                                        key={item.id}
                                        feedback={item}
                                        previousSubmissions={previousSubmissions}
                                        selectedFeedbackId={selectedFeedbackId}
                                        setSelectedFeedbackId={setSelectedFeedbackId}
                                    />
                                ) : (
                                    <CriterionFeedback
                                        key={item.id}
                                        assignment={assignment}
                                        feedback={item}
                                        previousSubmissions={previousSubmissions}
                                        selectedFeedbackId={selectedFeedbackId}
                                        setSelectedFeedbackId={setSelectedFeedbackId}
                                    />
                                )
                            )}
                    </Stack>
                </>
            )}
        </>
    );
};

const DetailedFeedback = ({ feedback, previousSubmissions, selectedFeedbackId, setSelectedFeedbackId }) => {
    const { t } = useTranslation('assignment');

    const [isHovered, setIsHovered] = useState(false);

    const previousImprovementSuggestions = previousSubmissions.map(({ ai }) =>
        ai.feedback.improvement_suggestions.find(({ criterion_id }) => criterion_id === feedback.criterion_id)
    );

    return (
        <CustomButtonBase
            component={'div'}
            role={'presentation'}
            onClick={() => setSelectedFeedbackId(selectedFeedbackId === feedback.id ? null : feedback.id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
                <Stack spacing={3}>
                    <Text align={'left'} rich>
                        {feedback.text}
                    </Text>
                    <Stack spacing={2}>
                        {previousImprovementSuggestions.reverse().map((feedback, index) => (
                            <Stack key={feedback.id} direction={'row'} spacing={4}>
                                <Stack sx={{ flexShrink: 0 }} alignItems={'center'} spacing={2}>
                                    <Tag type={'draft'} value={previousImprovementSuggestions.length - index} />
                                    <CustomBox />
                                </Stack>
                                <ViewMore
                                    sx={{ fontStyle: 'italic' }}
                                    TextComponentProps={{
                                        color: 'light',
                                        rich: true,
                                    }}
                                    variant={'primary'}
                                >
                                    {`${feedback.text || t(...noFeedbackTranslationArgs)}`}
                                </ViewMore>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
                {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
            </CustomPaper>
        </CustomButtonBase>
    );
};

const CriterionFeedback = ({
    assignment,
    feedback,
    previousSubmissions,
    selectedFeedbackId,
    setSelectedFeedbackId,
}) => {
    const { t } = useTranslation('assignment');

    const [isHovered, setIsHovered] = useState(false);

    const previousImprovementSuggestions = previousSubmissions.map(({ ai }) =>
        ai.feedback.improvement_suggestions.find(({ criterion_id }) => criterion_id === feedback.criterion_id)
    );

    return (
        <CustomButtonBase
            component={'div'}
            role={'presentation'}
            onClick={() => setSelectedFeedbackId(selectedFeedbackId === feedback.id ? null : feedback.id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
                <Stack>
                    <Text variant={'sh2'} weight={'semibold'}>
                        {assignment.rubric.criteria.find(({ id }) => id === feedback.criterion_id).title}
                    </Text>
                    <Divider sx={{ mt: 1.5, mb: 2 }} />
                    <Stack spacing={3}>
                        <Stack sx={{ p: 1 }} spacing={2}>
                            <Text variant={'p1'}>{t('feedback-section.positive-title', 'What you did well')}</Text>
                            {feedback.positive.text ? (
                                <ViewMore TextComponentProps={{ rich: true }}>{feedback.positive.text}</ViewMore>
                            ) : (
                                <Text color={'light'}>{t(...noFeedbackTranslationArgs)}</Text>
                            )}
                        </Stack>
                        <Stack
                            sx={(theme) => ({
                                borderRadius: '4px',
                                mx: -1,
                                p: 2,
                                background: theme.palette.ensightful.warning[200],
                            })}
                            spacing={2}
                        >
                            <Text variant={'p1'}>{t('feedback-section.improvement-title', 'How you can improve')}</Text>
                            {feedback.negative.text ? (
                                <ViewMore TextComponentProps={{ rich: true }}>{feedback.negative.text}</ViewMore>
                            ) : (
                                <Text color={'light'}>{t(...noFeedbackTranslationArgs)}</Text>
                            )}
                        </Stack>
                        <Stack spacing={2}>
                            {previousImprovementSuggestions.reverse().map((feedback, index) => (
                                <Stack key={feedback.id} direction={'row'} spacing={4}>
                                    <Stack sx={{ flexShrink: 0 }} alignItems={'center'} spacing={2}>
                                        <Tag type={'draft'} value={previousImprovementSuggestions.length - index} />
                                        <CustomBox />
                                    </Stack>
                                    <ViewMore
                                        sx={{ fontStyle: 'italic' }}
                                        TextComponentProps={{
                                            color: 'light',
                                            rich: true,
                                        }}
                                        variant={'primary'}
                                    >
                                        {`${feedback.positive.text || t(...noFeedbackTranslationArgs)}\n\n${feedback.negative.text || t(...noFeedbackTranslationArgs)}`}
                                    </ViewMore>
                                </Stack>
                            ))}
                        </Stack>
                    </Stack>
                </Stack>
                {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
            </CustomPaper>
        </CustomButtonBase>
    );
};

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 5),
        background: theme.palette.ensightful.primary[50],
        position: 'sticky',
        top: 0,
        zIndex: 1,
    };
});

const CenterText = styled(Text)(() => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '85%',
        transform: 'translate(-50%, -50%)',
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        width: '1px',
        height: '100%',
        background: theme.palette.ensightful.neutral[300],
    };
});

const CustomButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomPaper = styled(Paper)(({ theme, isActive }) => {
    return {
        position: 'relative',
        width: '100%',
        padding: theme.spacing(3),
        ...(isActive
            ? {
                  border: `2px solid ${theme.palette.ensightful.warning[700]}`,
              }
            : {
                  border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                  padding: `calc(${theme.spacing(3)} + 1px)`,
              }),
    };
});

const LeftBorderHighlight = styled(Box)(({ theme }) => {
    return {
        position: 'absolute',
        top: '50%',
        left: '-1px',
        height: 'calc(100% - 28px)',
        width: '2px',
        background: theme.palette.ensightful.warning[700],
        transform: 'translateY(-50%)',
    };
});

export default FeedbackRenderer;
